// Form state <-> contract input, and the material-change verdict (EVT-*, §10.8).
//
// Pure. No React, no Convex, no clock. Everything the event surfaces need to DECIDE
// lives here so it can be tested as data rather than through a rendered tree, and so
// the components stay about layout and words.
//
// ── VALIDATION IS THE CONTRACT'S, NOT OURS ──────────────────────────────────────
//
// `@herefor/contracts` holds the same zod schemas the Convex functions enforce, and
// this module calls them. There is deliberately no second set of rules here: a client
// that validated differently from the server would either reject something the server
// would accept (a form nobody can submit) or accept something it would refuse (a
// round trip that fails at the last moment). The bead names this explicitly — "no
// local validation duplicates" — and `eventData.test.ts` pins it by feeding the
// contract's own boundary cases through this module.
//
// ── THE MATERIAL VERDICT COMES FROM THE KERNEL ──────────────────────────────────
//
// `classifyEventChange` is the same function the backend runs to decide whether an
// edit flips somebody's commitment to "Please recheck". Running it here, live, as the
// organiser types, is what lets the form warn BEFORE the save rather than apologise
// after it (§10.8: no hidden invalidation). If the two ever disagreed the warning
// would be a lie, so there is one implementation and the UI borrows it.

import { events as eventContracts } from "@herefor/contracts";
import {
  classifyEventChange,
  instant,
  localDateOf,
  resolveWallClock,
  revision,
  timeZone,
  toWallClock,
  type ChangeClassification,
  type EventChangeInput,
  type EventRecord,
  type Instant,
  type TimeZoneId,
} from "@herefor/domain-kernel";

/** What the form holds while somebody is typing: strings, because inputs are strings. */
export interface EventFormValues {
  readonly title: string;
  readonly shareTitle: string;
  readonly category: string;
  /** `YYYY-MM-DDTHH:mm` local, as an `<input type="datetime-local">` produces. */
  readonly startsAtLocal: string;
  readonly endsAtLocal: string;
  readonly locationLabel: string;
  readonly locationPrivate: string;
  readonly practicalNote: string;
}

export const EMPTY_EVENT_FORM: EventFormValues = {
  title: "",
  shareTitle: "",
  category: "",
  startsAtLocal: "",
  endsAtLocal: "",
  locationLabel: "",
  locationPrivate: "",
  practicalNote: "",
};

/** One field's complaint, in the contract's own words. */
export interface FieldProblem {
  readonly field: string;
  readonly message: string;
}

export type FormValidation<T> =
  | { readonly ok: true; readonly input: T }
  | { readonly ok: false; readonly problems: readonly FieldProblem[] };

/**
 * The view an event detail surface renders.
 *
 * `isPast` is carried separately from `state` because Past is a display treatment
 * ALONGSIDE the state and never instead of it (plan §10.2) — a scheduled event whose
 * time has gone is both "In the plan" and "Past", and a card has to be able to say so.
 */
export interface EventDetailView {
  readonly eventId: string;
  readonly title: string;
  readonly shareTitle: string;
  readonly category: string;
  readonly windowLabel: string | null;
  /**
   * The stored instants, kept alongside the label.
   *
   * The label is for reading; these are what the kernel's classifier compares, and
   * re-parsing a formatted label to get them back would be a second, lossy source of
   * truth for the one value an incorrect verdict would misreport.
   */
  readonly startsAt: number | null;
  readonly endsAt: number | null;
  readonly locationLabel: string | null;
  readonly locationPrivate: string | null;
  readonly practicalNote: string | null;
  readonly state: "draft" | "scheduled" | "completed" | "cancelled";
  readonly revision: number;
  readonly isPast: boolean;
}

/** One entry of the append-only material history (EVT-014, plan §28.6). */
export interface EventHistoryEntryView {
  readonly revision: number;
  readonly changedAtLabel: string;
  /** What changed, as reason codes the surface turns into reviewed sentences. */
  readonly changes: readonly ("time" | "location" | "created")[];
}

/* ============================================================== form -> input === */

/**
 * Turn form strings into a `createEvent` input, validated by the contract.
 *
 * A blank optional field is OMITTED rather than sent as an empty string: the contract
 * treats absent as "not set", and an empty string as a value somebody typed. Sending
 * `""` for a location would record that the event is somewhere called nothing.
 */
export function toCreateInput(
  values: EventFormValues,
  circleId: string,
  zone: TimeZoneId,
): FormValidation<eventContracts.CreateEventInput> {
  const times = readTimes(values, zone);
  if (!times.ok) return times;

  const parsed = eventContracts.createEventInput.safeParse({
    circleId,
    title: values.title.trim(),
    shareTitle: values.shareTitle.trim(),
    category: values.category.trim(),
    ...(times.startsAt === null ? {} : { startsAt: times.startsAt }),
    ...(times.endsAt === null ? {} : { endsAt: times.endsAt }),
    ...optionalText("locationLabel", values.locationLabel),
    ...optionalText("locationPrivate", values.locationPrivate),
    ...optionalText("practicalNote", values.practicalNote),
  });

  return parsed.success
    ? { ok: true, input: parsed.data }
    : { ok: false, problems: problemsFrom(parsed.error.issues) };
}

/**
 * Turn form strings into an `updateEvent` input.
 *
 * An optional field the organiser CLEARED becomes `null`, not absent. The contract
 * gives three states on an edit — absent means "leave it alone", a value means "set
 * it", `null` means "remove it" — and without the third a practical note that turned
 * out to be wrong could only ever be overwritten with a space.
 */
export function toUpdateInput(
  values: EventFormValues,
  original: EventDetailView,
  circleId: string,
  zone: TimeZoneId,
): FormValidation<eventContracts.UpdateEventInput> {
  const times = readTimes(values, zone);
  if (!times.ok) return times;

  const parsed = eventContracts.updateEventInput.safeParse({
    circleId,
    eventId: original.eventId,
    expectedRevision: original.revision,
    title: values.title.trim(),
    shareTitle: values.shareTitle.trim(),
    category: values.category.trim(),
    ...(times.startsAt === null ? {} : { startsAt: times.startsAt }),
    ...(times.endsAt === null ? {} : { endsAt: times.endsAt }),
    locationLabel: clearable(values.locationLabel),
    locationPrivate: clearable(values.locationPrivate),
    practicalNote: clearable(values.practicalNote),
  });

  return parsed.success
    ? { ok: true, input: parsed.data }
    : { ok: false, problems: problemsFrom(parsed.error.issues) };
}

/* ============================================================ the live verdict === */

/**
 * Would saving this edit disturb somebody's agreement?
 *
 * Runs the kernel's own classifier over a before/after pair containing exactly the
 * fields that policy reads. No record identity crosses this field-only seam.
 *
 * Returns `null` while the form is not yet parseable — there is no honest verdict on
 * an edit that could not be saved anyway, and warning about one would train people to
 * ignore the warning.
 */
export function classifyPendingEdit(
  values: EventFormValues,
  original: EventDetailView,
  zone: TimeZoneId,
): ChangeClassification | null {
  const times = readTimes(values, zone);
  if (!times.ok) return null;

  return classifyEventChange(
    comparableRecord({
      startsAt: original.startsAt,
      endsAt: original.endsAt,
      locationLabel: original.locationLabel,
      locationPrivate: original.locationPrivate,
      state: original.state,
    }),
    comparableRecord({
      startsAt: times.startsAt,
      endsAt: times.endsAt,
      locationLabel: emptyToNull(values.locationLabel),
      locationPrivate: emptyToNull(values.locationPrivate),
      state: original.state,
    }),
  );
}

/**
 * A value carrying every field `classifyEventChange` reads and nothing else.
 * Cosmetic fields are held constant because this live verdict answers whether an
 * edit disturbs an agreement; the backend still classifies the complete saved edit.
 */
function comparableRecord(fields: {
  readonly startsAt: number | null;
  readonly endsAt: number | null;
  readonly locationLabel: string | null;
  readonly locationPrivate: string | null;
  readonly state: EventDetailView["state"];
}): EventChangeInput {
  return {
    title: "",
    shareTitle: "",
    category: "",
    ...(fields.startsAt === null ? {} : { startsAt: instant(fields.startsAt) }),
    ...(fields.endsAt === null ? {} : { endsAt: instant(fields.endsAt) }),
    ...(fields.locationLabel === null ? {} : { locationLabel: fields.locationLabel }),
    ...(fields.locationPrivate === null ? {} : { locationPrivate: fields.locationPrivate }),
    state: fields.state,
  };
}

/* ------------------------------------------------------------------ plumbing --- */

interface ReadTimes {
  readonly ok: true;
  readonly startsAt: number | null;
  readonly endsAt: number | null;
}

/**
 * Read both time fields, refusing a wall-clock reading that DST makes ambiguous.
 *
 * Twice a year a local time either does not exist or happens twice, and the kernel
 * makes the caller decide what that means rather than silently picking one (R-024
 * rates a DST bug that moves somebody's responsibility as Critical). A form is
 * exactly the place to hand it back to the person who typed it.
 */
function readTimes(
  values: EventFormValues,
  zone: TimeZoneId,
): ReadTimes | { readonly ok: false; readonly problems: readonly FieldProblem[] } {
  const problems: FieldProblem[] = [];
  const read = (raw: string, field: string): number | null => {
    if (raw.trim() === "") return null;
    const resolved = parseLocal(raw, zone);
    if (resolved === null) {
      problems.push({ field, message: "That time isn't one we can read." });
      return null;
    }
    return resolved;
  };

  const startsAt = read(values.startsAtLocal, "startsAt");
  const endsAt = read(values.endsAtLocal, "endsAt");
  if (problems.length > 0) return { ok: false, problems };
  return { ok: true, startsAt, endsAt };
}

/** `YYYY-MM-DDTHH:mm` in a zone -> an instant, or null when it is not a real moment. */
export function parseLocal(raw: string, zone: TimeZoneId): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u.exec(raw.trim());
  if (match === null) return null;

  const [, year, month, day, hour, minute] = match;
  const resolved = resolveWallClock(
    {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hour: Number(hour),
      minute: Number(minute),
    },
    zone,
  );

  // A gap is not a time. An ambiguous reading is two times, and picking one for
  // somebody is the bug R-024 describes — so the form is told to ask again.
  return resolved.kind === "ok" ? resolved.instant : null;
}

/** An instant -> the `YYYY-MM-DDTHH:mm` an input expects, in the circle's zone. */
export function toLocalInputValue(at: Instant, zone: TimeZoneId): string {
  const wall = toWallClock(at, zone);
  const pad = (value: number, width = 2) => String(value).padStart(width, "0");
  return `${pad(wall.year, 4)}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`;
}

function optionalText(field: string, raw: string): Record<string, string> {
  const trimmed = raw.trim();
  return trimmed === "" ? {} : { [field]: trimmed };
}

function clearable(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

function emptyToNull(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

function problemsFrom(
  issues: readonly { readonly path: readonly PropertyKey[]; readonly message: string }[],
): readonly FieldProblem[] {
  return issues.map((issue) => ({
    field: issue.path.length === 0 ? "form" : String(issue.path[issue.path.length - 1]),
    message: issue.message,
  }));
}

export { localDateOf, revision, timeZone };
export type { ChangeClassification, EventRecord, TimeZoneId };
